import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { ApiService } from '../../services/api.service';
import { CampaignService } from '../../services/campaign.service';
import {
  DashboardStatData,
  NewVsReturningData,
  RevenueStatisticsApiResponse,
  DashboardCustomerStatisticsApiResponse,
} from '../../shared/types';
import { calculateDynamicMax } from '../../shared/utils/chart.utils';
import { LocationService } from '../../services/location.service';
import { parsePeriodApiParam } from '../../shared/utils/dashboard-period-filter.util';

export interface SelectOption {
  value: string;
  label: string;
}
export interface ChartLabelsData {
  labels: (string | number)[];
  data: number[];
}
export interface PeriodApiParams {
  filterBy: string;
  startDate?: string;
  endDate?: string;
}

export interface RevenueStatsResult {
  stats: DashboardStatData[];
  rawData: RevenueStatisticsApiResponse;
}

export interface CustomerStatsResult {
  stats: DashboardStatData[];
  newVsReturning: NewVsReturningData;
  rawData: DashboardCustomerStatisticsApiResponse;
}

export interface CustomerTrendResult {
  labels: string[];
  newCustomers: number[];
  returningCustomers: number[];
}

export interface CarWashStatsResult {
  stats: DashboardStatData[];
  rawData: any;
}

export interface CampaignsByLocationResult {
  nameOptions: SelectOption[];
  platformOptions: SelectOption[];
  firstCampaignId: string;
}

@Injectable({ providedIn: 'root' })
export class DashboardDataService {
  constructor(
    private readonly apiService: ApiService,
    private readonly campaignService: CampaignService,
    private readonly locationService: LocationService
  ) {}

  private timezoneFor(location?: string | null): string {
    return this.locationService.getTimezoneForLocationId(location || undefined);
  }

  // ─── Utilities ────────────────────────────────────────────────────────────

  mapPeriodToApiFormat(period: string): string {
    return this.resolvePeriodParams(period).filterBy;
  }

  resolvePeriodParams(period: string): PeriodApiParams {
    const parsed = parsePeriodApiParam(period);
    return {
      filterBy: parsed.filterBy,
      startDate: parsed.startDate,
      endDate: parsed.endDate,
    };
  }

  getPeriodTextForDisplay(period: string): string {
    const displayPeriod = parsePeriodApiParam(period).displayPeriod;
    const labels: Record<string, string> = {
      today: 'Today',
      week: 'This week',
      month: 'This month',
      year: 'This year',
      Custom: 'Custom',
    };
    return labels[displayPeriod] ?? (displayPeriod === 'Custom' ? 'Custom' : 'Today');
  }

  getPeriodTextForComparison(period: string): string {
    const displayPeriod = parsePeriodApiParam(period).displayPeriod;
    const labels: Record<string, string> = {
      today: 'vs last day',
      week: 'vs last week',
      month: 'vs last month',
      year: 'vs last year',
      Custom: '',
    };
    return labels[displayPeriod] ?? '';
  }

  /**
   * Generates a list of unique hex color strings up to the requested count.
   * @example
   * generateUniqueColors(3)
   * ['#76DB9B', '#FF8780', '#8183F4']
   * @param {number} count - The number of colors to generate.
   * @returns {string[]} An array of unique hex color strings.
   */
  generateUniqueColors(count: number): string[] {
    const baseColors = [
      '#76DB9B',
      '#FF8780',
      '#8183F4',
      '#C996FA',
      '#F38EC0',
      '#10B981',
      '#F59E0B',
      '#06B6D4',
      '#84CC16',
      '#EF4444',
      '#8B5CF6',
      '#EC4899',
      '#14B8A6',
      '#F97316',
      '#6366F1',
      '#A855F7',
      '#EAB308',
      '#22C55E',
      '#3B82F6',
      '#8B5A2B',
    ];
    if (count <= baseColors.length) return baseColors.slice(0, count);
    const colors = [...baseColors];
    for (let i = baseColors.length; i < count; i++) {
      const hue = (i * 137.508) % 360;
      const s = 50 + (i % 3) * 15;
      const l = 45 + (Math.floor(i / 3) % 3) * 10;
      colors.push(this.hslToHex(hue, s, l));
    }
    return colors;
  }

  getVisitFrequencyColors(labels: (string | number)[]): string[] {
    const colorMap: Record<string, string> = {
      '7+ visits': '#8183F4',
      '4-6 visits': '#A855F7',
      '4-5 visits': '#a9df85',
      '2-3 visits': '#F2D066',
      '1 visit': '#76DB9B',
      '5+ visits': '#a9df85',
      '3-4 visits': '#FF8780',
      '2 visits': '#F2D066',
      '1-2 visits': '#76DB9B',
    };
    const defaults = [
      '#8183F4',
      '#FF8780',
      '#F2D066',
      '#76DB9B',
      '#A855F7',
      '#F59E0B',
      '#10B981',
      '#EF4444',
    ];
    return labels.map((label, i) => colorMap[String(label)] ?? defaults[i % defaults.length]);
  }

  formatPlatformName(platform: string): string {
    const nameMap: Record<string, string> = {
      email: 'Email',
      sms: 'Text Messaging',
      facebook: 'Facebook',
      google: 'Google Ads',
    };
    return nameMap[platform.toLowerCase()] ?? platform;
  }

  mapCampaignsToOptions(campaigns: any[]): SelectOption[] {
    return campaigns
      .filter(c => c.campaign_status === 'active')
      .map(c => ({ value: c.id, label: c.name }));
  }

  mapPlatformsToOptions(platforms: any[]): SelectOption[] {
    return platforms.map(p => ({ value: p.id, label: p.platform }));
  }

  private hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n: number) => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color)
        .toString(16)
        .padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  }

  // ─── Revenue Tab ──────────────────────────────────────────────────────────

  getRevenueStatistics(location: string, period: string): Observable<RevenueStatsResult> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getRevenueStatistics(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({ stats: this.mapRevenueStatsToCards(r.data!, period), rawData: r.data! }))
      );
  }

  getTopSellingService(
    location: string,
    period: string
  ): Observable<{ name: string; unitsSold: number; revenue: number }> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getTopSellingServices(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({
          name: r.data!.name,
          unitsSold: r.data!.total_orders,
          revenue: r.data!.total_revenue,
        }))
      );
  }

  getRevenueByServiceType(location: string, period: string): Observable<ChartLabelsData> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getRevenueByServiceType(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({ labels: r.data!.labels ?? [], data: r.data!.data ?? [] }))
      );
  }

  getRevenueTrend(
    location: string,
    period: string
  ): Observable<ChartLabelsData & { dynamicMax: number }> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getRevenueTrend(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({
          labels: r.data!.labels,
          data: r.data!.data,
          dynamicMax: calculateDynamicMax(r.data!.data),
        }))
      );
  }

  /**
   * Retrieves average revenue trend data for a given location and period.
   * @example
   * etAverageRevenueTrend('New York', 'last_30_days')
   * { title: 'Average Revenue Trend', labels: [], data: [], dynamicMax: 100 }
   * @param {string} location - Location to fetch revenue trend data for.
   * @param {string} period - Time period to retrieve the trend for.
   * @returns {Observable<ChartLabelsData & { title: string; dynamicMax: number }>} Observable emitting the average revenue trend chart data.
   **/
  getAverageRevenueTrend(
    location: string,
    period: string
  ): Observable<ChartLabelsData & { title: string; dynamicMax: number }> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getAverageRevenueTrend(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({
          title: r.data!.title,
          labels: r.data!.labels,
          data: r.data!.data,
          dynamicMax: calculateDynamicMax(r.data!.data),
        }))
      );
  }

  // ─── Customer Tab ─────────────────────────────────────────────────────────

  /**
   * Retrieves customer statistics for a given location and period, returning mapped dashboard data as an observable.
   * @example
   * etCustomerStatistics('US', 'last_30_days')
   * { stats: [...], newVsReturning: {...}, rawData: {...} }
   * @param {string} location - Location identifier used to fetch customer statistics.
   * @param {string} period - Time period used to filter and map the statistics request.
   * @returns {Observable<CustomerStatsResult>} Observable emitting the customer statistics result object.
   **/
  getCustomerStatistics(location: string, period: string): Observable<CustomerStatsResult> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getCustomerStatistics(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({
          stats: this.mapCustomerStatsToCards(r.data!, period),
          newVsReturning: this.mapCustomerStatsToNewVsReturning(r.data!),
          rawData: r.data!,
        }))
      );
  }

  /**
   * Retrieves customer trend data for a given location and period.
   * @example
   * etCustomerTrend('us-east', 'last_30_days')
   * { labels: [], newCustomers: [], returningCustomers: [] }
   * @param {string} location - Location identifier used to fetch customer trend data.
   * @param {string} period - Time period used to filter the customer trend data.
   * @returns {Observable<CustomerTrendResult>} Observable emitting the mapped customer trend result.
   **/
  getCustomerTrend(location: string, period: string): Observable<CustomerTrendResult> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getCustomerTrend(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({
          labels: r.data!.labels,
          newCustomers: r.data!.newCustomers,
          returningCustomers: r.data!.returningCustomers,
        }))
      );
  }

  getCustomerVisitFrequency(location: string, period: string): Observable<ChartLabelsData> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getCustomerVisitFrequency(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({ labels: r.data!.labels ?? [], data: r.data!.data ?? [] }))
      );
  }

  // ─── Car Wash Tab ─────────────────────────────────────────────────────────

  getWashesByServiceType(location: string, period: string): Observable<ChartLabelsData> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getWashesByServiceType(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({ labels: r.data!.labels ?? [], data: r.data!.data ?? [] }))
      );
  }

  /**
   * Retrieves wash count trend chart data for a given location and period.
   * @example
   * etWashCountTrend('new-york', 'last_7_days')
   * { labels: ['Mon', 'Tue'], data: [12, 18], dynamicMax: 20 }
   * @param {string} location - Location identifier used to fetch the trend data.
   * @param {string} period - Time period used to map the request to the API format.
   * @returns {Observable<ChartLabelsData & { dynamicMax: number }>} Observable emitting chart labels, data, and calculated maximum value.
   **/
  getWashCountTrend(
    location: string,
    period: string
  ): Observable<ChartLabelsData & { dynamicMax: number }> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getWashCountTrend(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => ({
          labels: r.data!.labels,
          data: r.data!.data,
          dynamicMax: calculateDynamicMax(r.data!.data),
        }))
      );
  }

  getCarWashStatistics(location: string, period: string): Observable<CarWashStatsResult> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService.getWashCount(filterBy, location, timezone, { startDate, endDate }).pipe(
      filter(r => r.status && !!r.data),
      map(r => ({ stats: this.mapWashCountToCards(r.data!, period), rawData: r.data! }))
    );
  }

  // ─── Campaign Tab ─────────────────────────────────────────────────────────

  getCampaignStatistics(location: string, period: string): Observable<DashboardStatData[]> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getCampaignStatistics(filterBy, location, timezone, { startDate, endDate })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => this.mapCampaignStatsToCards(r.data!, period))
      );
  }

  /**
   * The campaign dropdown only ever lists *active* campaigns, so the selection it
   * starts on has to come from that same filtered list. Seeding it from the raw
   * response instead meant that whenever the location's first campaign was archived
   * the tab opened on an id absent from its own options: the dropdown rendered blank
   * and every campaign request went out for a campaign the user could not see or
   * change. The platform options have the same origin and so get the same treatment.
   */
  getCampaignsByLocation(location: string): Observable<CampaignsByLocationResult> {
    return this.apiService.getCampaignsByLocation(location).pipe(
      filter(r => r.status && !!r.data && r.data.length > 0),
      map(r => {
        const activeCampaigns = (r.data ?? []).filter(c => c.campaign_status === 'active');
        return {
          nameOptions: this.mapCampaignsToOptions(activeCampaigns),
          platformOptions: this.mapPlatformsToOptions(activeCampaigns[0]?.platforms ?? []),
          firstCampaignId: activeCampaigns[0]?.id ?? '',
        };
      })
    );
  }

  /**
   * Retrieves campaign platforms as selectable options for a given campaign.
   * @example
   * etCampaignPlatforms(campaignId)
   * Observable<SelectOption[]>
   * @param {string} campaignId - The campaign identifier used to fetch platform data.
   * @returns {Observable<SelectOption[]>} An observable that emits an array of platform select options.
   */
  getCampaignPlatforms(campaignId: string): Observable<SelectOption[]> {
    return this.campaignService.getCampaign(campaignId).pipe(
      filter(r => r.status && !!r.data),
      map(r => {
        let platforms: any[] = r.data!.platforms ?? [];
        if (platforms.length > 0 && typeof platforms[0] === 'string') {
          platforms = platforms.map((p: string) => ({
            id: p,
            platform: this.formatPlatformName(p),
          }));
        }
        return this.mapPlatformsToOptions(platforms);
      })
    );
  }

  /**
   * Retrieves campaign performance chart data for a given platform, location, period, and campaign.
   * @example
   * etCampaignPerformance('google', 'us', '30d', 'camp_123')
   * { labels: ['Mon', 'Tue'], data: [10, 15], dynamicMax: 15 }
   * @param {string} platformId - Platform identifier used to fetch the performance data.
   * @param {string} location - Location identifier used for the request.
   * @param {string} period - Time period used to map the API filter format.
   * @param {string} campaignId - Campaign identifier to retrieve performance metrics for.
   * @returns {Observable<(ChartLabelsData & { dynamicMax: number }) | null>} Observable that emits chart labels, data, and computed maximum value, or null if no valid data is returned.
   */
  getCampaignPerformance(
    platformId: string,
    location: string,
    period: string,
    campaignId: string
  ): Observable<(ChartLabelsData & { dynamicMax: number }) | null> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getCampaignPerformance(platformId, location, filterBy, campaignId, timezone, {
        startDate,
        endDate,
      })
      .pipe(
        map(r => {
          if (r.status && r.data?.labels && r.data?.data?.length > 0) {
            return {
              labels: r.data.labels,
              data: r.data.data,
              dynamicMax: calculateDynamicMax(r.data.data),
            };
          }
          return null;
        })
      );
  }

  /**
   * Retrieves campaign funnel chart data for a given platform, location, period, and campaign.
   * @example
   * etCampaignFunnel(platformId, location, period, campaignId)
   * { labels: [...], data: [...] } | null
   * @param {string} platformId - Platform identifier used to fetch the campaign funnel data.
   * @param {string} location - Location identifier used to scope the data request.
   * @param {string} period - Time period used to map the request into API format.
   * @param {string} campaignId - Campaign identifier used to retrieve funnel metrics.
   * @returns {Observable<ChartLabelsData | null>} An observable that emits chart label/data objects or null when no valid data is available.
   */
  getCampaignFunnel(
    platformId: string,
    location: string,
    period: string,
    campaignId: string
  ): Observable<ChartLabelsData | null> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getCampaignFunnel(platformId, location, filterBy, campaignId, timezone, {
        startDate,
        endDate,
      })
      .pipe(
        map(r => {
          if (r.status && r.data?.labels && r.data?.data?.length > 0) {
            return { labels: r.data.labels, data: r.data.data };
          }
          return null;
        })
      );
  }

  getCampaignOverallStatistics(
    platformId: string,
    location: string,
    period: string,
    campaignId: string
  ): Observable<DashboardStatData[]> {
    const { filterBy, startDate, endDate } = this.resolvePeriodParams(period);
    const timezone = this.timezoneFor(location);
    return this.apiService
      .getCampaignOverallStatistics(platformId, location, filterBy, campaignId, timezone, {
        startDate,
        endDate,
      })
      .pipe(
        filter(r => r.status && !!r.data),
        map(r => this.mapCampaignOverallStatsToCards(r.data!, period))
      );
  }

  // ─── Private mapping methods ──────────────────────────────────────────────

  /**
   * Maps revenue statistics API response into dashboard card data for a selected period.
   * @example
   * mapRevenueStatsToCards(data, period)
   * [{ title: 'Total Revenue', value: '$1,000', ... }]
   * @param {RevenueStatisticsApiResponse} data - Revenue statistics response containing values and growth rates.
   * @param {string} period - Selected period used to generate comparison text.
   * @returns {DashboardStatData[]} Array of dashboard statistic card objects.
   **/
  private mapRevenueStatsToCards(
    data: RevenueStatisticsApiResponse,
    period: string
  ): DashboardStatData[] {
    const periodText = this.getPeriodTextForComparison(period);
    return [
      {
        title: 'Total Revenue',
        value: data.revenue.value === 0 ? '-' : `$${data.revenue.value.toLocaleString()}`,
        percentage:
          data.revenue.growth > 0 ? `+${data.revenue.growth}%` : `${data.revenue.growth}%`,
        periodText,
        gradient: 'linear-gradient(180deg, #A9DF85 0%, #6DA548 100%)',
        icon: 'assets/dashboard/r-1.png',
        tooltip: 'Total revenue generated from the selected location in the selected period.',
      },
      {
        title: 'Cash Collection',
        value:
          data.cash_collection.value === 0
            ? '-'
            : `$${data.cash_collection.value.toLocaleString()}`,
        percentage:
          data.cash_collection.growth > 0
            ? `+${data.cash_collection.growth}%`
            : `${data.cash_collection.growth}%`,
        periodText,
        gradient: 'linear-gradient(rgb(142, 217, 246) 0%, rgb(73, 163, 215) 100%)',
        icon: 'assets/dashboard/r-1.png',
        tooltip: 'Total cash payments collected in the selected period.',
      },
      {
        title: 'Card Collection',
        value:
          data.card_collection.value === 0
            ? '-'
            : `$${data.card_collection.value.toLocaleString()}`,
        percentage:
          data.card_collection.growth > 0
            ? `+${data.card_collection.growth}%`
            : `${data.card_collection.growth}%`,
        periodText,
        gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
        icon: 'assets/dashboard/r-1.png',
        tooltip: 'Total card payments collected in the selected period.',
      },
      {
        title: 'Prepaid Order Count',
        value:
          data.prepaid_order_count.value === 0
            ? '-'
            : data.prepaid_order_count.value.toLocaleString(),
        percentage:
          data.prepaid_order_count.growth > 0
            ? `+${data.prepaid_order_count.growth}%`
            : `${data.prepaid_order_count.growth}%`,
        periodText,
        gradient: 'linear-gradient(rgb(121, 136, 223) 0%, rgb(59, 109, 218) 100%)',
        icon: 'assets/dashboard/r-2.png',
        tooltip: 'Number of prepaid wash books sold (paid) in the selected period.',
      },
      {
        title: 'Avg. Revenue/Wash',
        value: data.avg_revenue_per_wash.value === 0 ? '-' : `$${data.avg_revenue_per_wash.value}`,
        percentage:
          data.avg_revenue_per_wash.growth > 0
            ? `+${data.avg_revenue_per_wash.growth}%`
            : `${data.avg_revenue_per_wash.growth}%`,
        periodText,
        gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
        icon: 'assets/dashboard/r-3.png',
        tooltip: 'Average revenue earned per car wash in the selected period.',
      },
      {
        title: 'Upsell Conversion',
        value: `${data.subscription_count.value.toLocaleString()}%`,
        percentage:
          data.subscription_count.growth > 0
            ? `+${data.subscription_count.growth}%`
            : `${data.subscription_count.growth}%`,
        periodText,
        gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
        icon: 'assets/dashboard/r-4.png',
        tooltip: 'Percentage of car washes that resulted in an upsell during the selected period.',
      },
    ];
  }

  /**
   * Maps customer statistics API response data into dashboard stat card objects.
   * @example
   * mapCustomerStatsToCards(data, period)
   * [{ title: 'Total Active Members', value: '1,200', percentage: '+5%', periodText: 'Last 30 days', gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)', icon: 'assets/dashboard/c-1.png', tooltip: 'Number of customers with an active membership.' }]
   * @param {DashboardCustomerStatisticsApiResponse} data - Customer statistics response data from the API.
   * @param {string} period - The reporting period used to generate display text.
   * @returns {DashboardStatData[]} Array of dashboard statistic card data objects.
   **/
  private mapCustomerStatsToCards(
    data: DashboardCustomerStatisticsApiResponse,
    period: string
  ): DashboardStatData[] {
    return [
      {
        title: 'Total Active Members',
        value: data.active_customers === 0 ? '-' : data.active_customers.toLocaleString(),
        percentage: data.growth
          ? Number(data.growth) > 0
            ? `+${data.growth}%`
            : `${data.growth}%`
          : '',
        periodText: this.getPeriodTextForDisplay(period),
        gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)',
        icon: 'assets/dashboard/c-1.png',
        // Note: when the count includes imported memberships it covers every
        // location, because those carry none — the API flags that as
        // `active_members_tenant_wide`. The card does not currently say so.
        tooltip: 'Number of customers with an active membership.',
      },
      {
        title: 'Customer Satisfaction',
        value: data.customer_satisfaction > 0 ? `${data.customer_satisfaction}/5` : '-',
        percentage: '',
        periodText: 'As of latest update',
        gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
        icon: 'assets/dashboard/c-2.png',
        tooltip: 'Average customer rating based on the latest available feedback.',
      },
      {
        title: 'Membership Renewal',
        value: Number(data.membership_renewal) === 0 ? '-' : `${data.membership_renewal}%`,
        percentage: '',
        periodText: this.getPeriodTextForDisplay(period),
        gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
        icon: 'assets/dashboard/c-3.png',
        tooltip: 'Percentage of membership renewal charges that succeeded in the selected period.',
      },
    ];
  }

  /**
   * Maps dashboard customer statistics into a NewVsReturningData display object.
   * @example
   * mapCustomerStatsToNewVsReturning(data)
   * {
   *   title: 'New vs Returning',
   *   newCustomers: { count: '12', percentage: 40 },
   *   returningCustomers: { count: '18', percentage: 60 },
   *   gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
   *   tooltip: 'Number of new vs returning customers each month.'
   * }
   * @param {DashboardCustomerStatisticsApiResponse} data - Customer statistics API response containing new and returning customer counts and percentages.
   * @returns {NewVsReturningData} Formatted new vs returning customer statistics for dashboard display.
   **/
  private mapCustomerStatsToNewVsReturning(
    data: DashboardCustomerStatisticsApiResponse
  ): NewVsReturningData {
    return {
      title: 'New vs Returning',
      newCustomers: {
        count: data.new_customers === 0 ? '-' : data.new_customers.toString(),
        percentage: data.new_customers_pct,
      },
      returningCustomers: {
        count: data.returning_customers === 0 ? '-' : data.returning_customers.toString(),
        percentage: data.returning_customers_pct,
      },
      gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
      tooltip: 'Number of new vs returning customers each month.',
    };
  }

  /**
   * Maps wash count analytics data into dashboard statistic cards for display.
   * @example
   * mapWashCountToCards(data, period)
   * [{ title: 'Total Washes', value: '120', percentage: '', periodText: 'This Month', gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)', icon: 'assets/dashboard/w-1.png', tooltip: 'Car washes completed in the selected time period: single washes, membership redemptions, prepaid redemptions, rewashes and fleet washes.' }]
   * @param {any} data - Wash analytics data containing wash count, average washes, and popular service details.
   * @param {string} period - Selected reporting period used to generate the display text.
   * @returns {DashboardStatData[]} Array of dashboard statistic card objects for wash metrics.
   **/
  private mapWashCountToCards(data: any, period: string): DashboardStatData[] {
    const periodText = this.getPeriodTextForDisplay(period);
    return [
      {
        title: 'Total Washes',
        value: data.wash_count ? data.wash_count.toString() : '-',
        percentage: '',
        periodText,
        gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)',
        icon: 'assets/dashboard/w-1.png',
        tooltip:
          'Car washes completed in the selected time period: single washes, membership redemptions, prepaid redemptions, rewashes and fleet washes.',
      },
      {
        title: 'Avg. Washes/Hr',
        value: data.average_washes ? data.average_washes.toString() : '-',
        percentage: '',
        periodText: 'Based on operating hours',
        gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
        icon: 'assets/dashboard/w-2.png',
        tooltip: 'Average number of car washes completed every hour.',
      },
      {
        title: 'Popular Service',
        value: data.popular_service?.package_name || '-',
        percentage: '',
        periodText: data.popular_service?.order_percentage
          ? `${data.popular_service.order_percentage}% of all services`
          : '',
        gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
        icon: 'assets/dashboard/w-4.png',
        tooltip: 'The most frequently purchased wash service by customers.',
      },
    ];
  }

  /**
   * Maps campaign statistics into dashboard card data for display.
   * @example
   * mapCampaignStatsToCards(data, period)
   * [{ title: 'Emails', value: '120', ... }, ...]
   * @param {any} data - Campaign statistics object containing sent counts, totals, spend, and conversion rate.
   * @param {string} period - Selected reporting period used to format the period text.
   * @returns {DashboardStatData[]} Array of dashboard stat card objects representing campaign metrics.
   */
  private mapCampaignStatsToCards(data: any, period: string): DashboardStatData[] {
    const periodText = this.getPeriodTextForDisplay(period);
    const emailProgress =
      data.total_emails > 0 ? (parseInt(data.sent_emails) / parseInt(data.total_emails)) * 100 : 0;
    const smsProgress =
      data.total_sms > 0 ? (parseInt(data.sent_sms) / parseInt(data.total_sms)) * 100 : 0;
    return [
      {
        title: 'Emails',
        value: `${data.sent_emails}`,
        percentage: '',
        periodText: 'Sent',
        gradient: 'linear-gradient(180deg, #85DF9C 0%, #48A55F 100%)',
        icon: 'assets/dashboard/cm-1.png',
        tooltip: 'Total number of marketing emails sent vs planned.',
        showProgressBar: true,
        progressPercentage: emailProgress,
      },
      {
        title: 'Text Messages',
        value: `${data.sent_sms}`,
        percentage: '',
        periodText: 'Sent',
        gradient: 'linear-gradient(180deg, #A3C7FF 0%, #5186D9 100%)',
        icon: 'assets/dashboard/cm-2.png',
        tooltip: 'Total number of Text Messages sent vs planned.',
        showProgressBar: true,
        progressPercentage: smsProgress,
      },
      {
        title: 'Marketing Spend',
        value: `$${data.total_spent}`,
        percentage: '',
        periodText,
        gradient: 'linear-gradient(180deg, #AFA5F6 0%, #8878F2 100%)',
        icon: 'assets/dashboard/cm-3.png',
        tooltip: 'Total amount spent for marketing campaigns in the selected period.',
      },
      {
        title: 'Avg. Conversion',
        value: `${data.conversion_rate}%`,
        percentage: '',
        periodText: 'Campaign performance',
        gradient: 'linear-gradient(180deg, #8ED9F6 0%, #49A3D7 100%)',
        icon: 'assets/dashboard/cm-4.png',
        tooltip: 'Percentage of users who purchased a wash after engaging with the campaign.',
      },
    ];
  }

  /**
   * Maps campaign overall statistics into dashboard card data for display.
   * @example
   * mapCampaignOverallStatsToCards(data, period)
   * [{ title: 'Returning Customers', value: '10', ... }, { title: 'Revenue Generated', value: '$1,250.00', ... }]
   * @param {any} data - Raw campaign statistics object containing returning customers and revenue values.
   * @param {string} period - The selected reporting period used to generate display text.
   * @returns {DashboardStatData[]} An array of dashboard card objects for the campaign summary.
   **/
  private mapCampaignOverallStatsToCards(data: any, period: string): DashboardStatData[] {
    const periodText = this.getPeriodTextForDisplay(period);
    const formatRevenue = (value: any): string => {
      if (value == null) return '$0';
      const num = typeof value === 'string' ? parseFloat(value) : value;
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    const formatCustomers = (value: any): string =>
      value == null ? '0' : typeof value === 'string' ? value : value.toString();
    return [
      {
        title: 'Returning Customers',
        value: formatCustomers(data.returning_customers || 0),
        percentage: '',
        periodText,
        gradient: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%)',
        icon: 'assets/dashboard/c-1.png',
        tooltip: 'Number of customers who returned for another wash during the selected period.',
        textColor: '#73A0E9',
      },
      {
        title: 'Revenue Generated',
        value: formatRevenue(data.total_revenue || 0),
        percentage: '',
        periodText,
        gradient: 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%)',
        icon: 'assets/dashboard/r-1.png',
        tooltip: 'Total revenue from completed car washes in the selected period.',
        textColor: '#079271',
      },
    ];
  }
}
